// Web stand-in for react-native-image-picker (aliased in
// webpack.config.web.js). Backed by a real <input type="file"> picker, not
// a dummy — produces a genuine blob: URI usable by the rest of the upload
// pipeline (src/utils/imagePipeline.web.ts, src/firebase.ts), matching the
// shape (`{ didCancel, assets }`) every call site in src/utils/permissions.ts
// already expects from the native library.
export type ImagePickerAsset = {
  uri: string;
  fileName?: string;
  type?: string;
  fileSize?: number;
};

export type ImagePickerResponse = {
  didCancel?: boolean;
  errorCode?: string;
  errorMessage?: string;
  assets?: ImagePickerAsset[];
};

export type CameraOptions = Record<string, unknown>;
export type ImageLibraryOptions = Record<string, unknown>;

function pickFile(capture?: 'user' | 'environment'): Promise<File | null> {
  return new Promise(resolve => {
    if (typeof document === 'undefined') {
      resolve(null);
      return;
    }
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    if (capture) input.setAttribute('capture', capture);
    input.onchange = () => resolve(input.files?.[0] ?? null);
    // <input type="file"> has no reliable native "cancel" event across
    // browsers — approximate launchImageLibrary/launchCamera's "user
    // cancelled" convention (didCancel: true) by resolving null once the
    // window regains focus without a file having been chosen.
    const onFocus = () => {
      window.removeEventListener('focus', onFocus);
      window.setTimeout(() => {
        if (!input.files?.length) resolve(null);
      }, 300);
    };
    window.addEventListener('focus', onFocus);
    input.click();
  });
}

function toResponse(file: File | null): ImagePickerResponse {
  if (!file) return { didCancel: true };
  return {
    assets: [
      {
        uri: URL.createObjectURL(file),
        fileName: file.name,
        type: file.type,
        fileSize: file.size,
      },
    ],
  };
}

export async function launchImageLibrary(_options: ImageLibraryOptions): Promise<ImagePickerResponse> {
  const file = await pickFile();
  return toResponse(file);
}

export async function launchCamera(_options: CameraOptions): Promise<ImagePickerResponse> {
  const file = await pickFile('environment');
  return toResponse(file);
}
